import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Pencil, Trash2, Check, X, ChevronDown, ChevronRight } from 'lucide-react';
import { TYPE_OPTIONS, TYPE_BADGE_STYLES, TYPE_LABEL } from './constants';

export function CategoryRow({
  cat, isExpanded, onToggleExpand,
  editingCat, setEditingCat, onSaveCatEdit, onChangeType, onDeleteCat,
  editingSub, setEditingSub, onRenameSub,
  addingSub, setAddingSub, newSubName, setNewSubName, onAddSub,
  addingChild, setAddingChild, newChildName, setNewChildName, onAddChild,
  onRequestDelete,
}) {
  const subs = cat.subcategories || [];
  const isEditingCat = editingCat?.id === cat.id;

  return (
    <div className="border border-border/60 rounded-lg bg-white" data-testid="category-manage-item">
      {/* Category row */}
      <div className="flex items-center gap-2 px-3 py-2">
        <button onClick={onToggleExpand} className="text-muted-foreground hover:text-foreground" data-testid="category-expand-toggle">
          {isExpanded ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
        </button>
        {isEditingCat ? (
          <>
            <Input
              value={editingCat.name}
              onChange={e => setEditingCat({ ...editingCat, name: e.target.value })}
              onKeyDown={e => {
                if (e.key === 'Enter') { e.preventDefault(); onSaveCatEdit(cat); }
                if (e.key === 'Escape') setEditingCat(null);
              }}
              className="h-7 text-xs flex-1"
              autoFocus
              data-testid="edit-category-input"
            />
            <Select value={editingCat.type} onValueChange={v => setEditingCat({ ...editingCat, type: v })}>
              <SelectTrigger className="h-7 w-28 text-xs" data-testid="edit-category-type"><SelectValue /></SelectTrigger>
              <SelectContent>
                {TYPE_OPTIONS.map(opt => <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>)}
              </SelectContent>
            </Select>
            <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-primary" onClick={() => onSaveCatEdit(cat)} data-testid="save-category-btn"><Check className="h-3.5 w-3.5" /></Button>
            <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => setEditingCat(null)}><X className="h-3.5 w-3.5" /></Button>
          </>
        ) : (
          <>
            <span className="text-sm font-semibold text-[#1f2933] flex-1" data-testid="category-name">{cat.name}</span>
            <Badge variant="outline" className={`text-[10px] font-medium h-5 px-1.5 ${TYPE_BADGE_STYLES[cat.type || 'none']}`} data-testid="category-type-badge">
              {TYPE_LABEL[cat.type || 'none']}
            </Badge>
            <Select value={cat.type || 'none'} onValueChange={v => onChangeType(cat, v)}>
              <SelectTrigger className="h-6 w-8 p-0 border-0 shadow-none bg-transparent hover:bg-muted/50" data-testid="quick-type-selector">
                <ChevronDown className="h-3 w-3 text-muted-foreground" />
              </SelectTrigger>
              <SelectContent>
                {TYPE_OPTIONS.map(opt => <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>)}
              </SelectContent>
            </Select>
            <span className="text-[10px] text-muted-foreground tabular-nums">{subs.length} sub</span>
            <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-muted-foreground hover:text-primary" onClick={() => setEditingCat({ id: cat.id, name: cat.name, type: cat.type || 'none' })} data-testid="rename-category-btn">
              <Pencil className="h-3.5 w-3.5" />
            </Button>
            <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-muted-foreground hover:text-red-600" onClick={() => onRequestDelete({ type: 'category', cat })} data-testid="delete-category-btn">
              <Trash2 className="h-3.5 w-3.5" />
            </Button>
          </>
        )}
      </div>

      {/* Subcategories */}
      {isExpanded && (
        <div className="px-3 pb-2 pt-0 space-y-1.5 border-t border-border/30 bg-[#fafaf7]/50">
          {subs.map((sub, subIdx) => (
            <SubRow
              key={sub.name}
              cat={cat}
              sub={sub}
              subIdx={subIdx}
              editingSub={editingSub}
              setEditingSub={setEditingSub}
              onRenameSub={onRenameSub}
              onRequestDelete={onRequestDelete}
              addingChild={addingChild}
              setAddingChild={setAddingChild}
              newChildName={newChildName}
              setNewChildName={setNewChildName}
              onAddChild={onAddChild}
            />
          ))}

          {/* Add subcategory */}
          {addingSub === cat.id ? (
            <div className="pl-5 flex items-center gap-1 pt-1">
              <Input
                value={newSubName}
                onChange={e => setNewSubName(e.target.value)}
                onKeyDown={e => {
                  if (e.key === 'Enter') { e.preventDefault(); onAddSub(cat); }
                  if (e.key === 'Escape') { setAddingSub(null); setNewSubName(''); }
                }}
                placeholder="New subcategory name…"
                className="h-6 text-xs flex-1"
                autoFocus
                data-testid="new-sub-input"
              />
              <Button size="sm" variant="ghost" className="h-6 w-6 p-0 text-primary" onClick={() => onAddSub(cat)}><Check className="h-3 w-3" /></Button>
              <Button size="sm" variant="ghost" className="h-6 w-6 p-0" onClick={() => { setAddingSub(null); setNewSubName(''); }}><X className="h-3 w-3" /></Button>
            </div>
          ) : (
            <button
              onClick={() => { setAddingSub(cat.id); setNewSubName(''); }}
              className="pl-5 text-[11px] text-muted-foreground hover:text-primary transition-colors pt-0.5"
              data-testid="add-sub-btn"
            >
              + Add subcategory
            </button>
          )}
        </div>
      )}
    </div>
  );
}

function SubRow({
  cat, sub, subIdx,
  editingSub, setEditingSub, onRenameSub, onRequestDelete,
  addingChild, setAddingChild, newChildName, setNewChildName, onAddChild,
}) {
  const isEditing = editingSub?.catId === cat.id && editingSub?.subIdx === subIdx;
  const isAddingChild = addingChild?.catId === cat.id && addingChild?.subIdx === subIdx;
  return (
    <div className="pl-5">
      <div className="flex items-center gap-2 py-1">
        {isEditing ? (
          <>
            <Input
              value={editingSub.name}
              onChange={e => setEditingSub({ ...editingSub, name: e.target.value })}
              onKeyDown={e => {
                if (e.key === 'Enter') { e.preventDefault(); onRenameSub(cat, subIdx); }
                if (e.key === 'Escape') setEditingSub(null);
              }}
              className="h-6 text-xs flex-1"
              autoFocus
              data-testid="edit-sub-input"
            />
            <Button size="sm" variant="ghost" className="h-6 w-6 p-0 text-primary" onClick={() => onRenameSub(cat, subIdx)}><Check className="h-3 w-3" /></Button>
            <Button size="sm" variant="ghost" className="h-6 w-6 p-0" onClick={() => setEditingSub(null)}><X className="h-3 w-3" /></Button>
          </>
        ) : (
          <>
            <span className="text-xs text-[#5b6773] flex-1" data-testid="sub-name">{sub.name}</span>
            {(sub.children || []).length > 0 && (
              <span className="text-[10px] text-muted-foreground">{sub.children.length} detail{sub.children.length === 1 ? '' : 's'}</span>
            )}
            <Button size="sm" variant="ghost" className="h-6 w-6 p-0 text-muted-foreground hover:text-primary" onClick={() => setEditingSub({ catId: cat.id, subIdx, name: sub.name })} data-testid="rename-sub-btn">
              <Pencil className="h-3 w-3" />
            </Button>
            <Button size="sm" variant="ghost" className="h-6 w-6 p-0 text-muted-foreground hover:text-red-600" onClick={() => onRequestDelete({ type: 'sub', cat, subIdx })} data-testid="delete-sub-btn">
              <Trash2 className="h-3 w-3" />
            </Button>
          </>
        )}
      </div>

      {(sub.children || []).length > 0 && (
        <div className="pl-5 flex flex-wrap gap-1 pb-1">
          {sub.children.map(child => (
            <Badge key={child} variant="outline" className="text-[10px] font-normal h-5 pl-2 pr-1 gap-1" data-testid="child-badge">
              {child}
              <button
                onClick={() => onRequestDelete({ type: 'child', cat, subIdx, child })}
                className="ml-0.5 rounded-full hover:bg-red-100 p-0.5"
                data-testid="delete-child-btn"
              >
                <X className="h-2.5 w-2.5" />
              </button>
            </Badge>
          ))}
        </div>
      )}

      {isAddingChild ? (
        <div className="pl-5 flex items-center gap-1 pb-1">
          <Input
            value={newChildName}
            onChange={e => setNewChildName(e.target.value)}
            onKeyDown={e => {
              if (e.key === 'Enter') { e.preventDefault(); onAddChild(cat, subIdx); }
              if (e.key === 'Escape') { setAddingChild(null); setNewChildName(''); }
            }}
            placeholder="New detail name…"
            className="h-6 text-[11px] flex-1"
            autoFocus
            data-testid="new-child-input"
          />
          <Button size="sm" variant="ghost" className="h-6 w-6 p-0 text-primary" onClick={() => onAddChild(cat, subIdx)}><Check className="h-3 w-3" /></Button>
          <Button size="sm" variant="ghost" className="h-6 w-6 p-0" onClick={() => { setAddingChild(null); setNewChildName(''); }}><X className="h-3 w-3" /></Button>
        </div>
      ) : (
        <button
          onClick={() => { setAddingChild({ catId: cat.id, subIdx }); setNewChildName(''); }}
          className="pl-5 text-[10px] text-muted-foreground hover:text-primary transition-colors"
          data-testid="add-child-btn"
        >
          + Add detail
        </button>
      )}
    </div>
  );
}
